interface GameResponse {
    keysStolen: number;
    isComplete: boolean;
    guardPosition: number;
}

import { GameRouteService } from "../../services/GameRouteService";

export class KeyTheftGameComponent extends HTMLElement {
    private keysStolen: number = 0;
    private guardPosition: number = 2; // Guard starts in the middle position (0-4)
    private shadow: ShadowRoot;
    private playerPosition: number = 0; // Player starts at position 0
    private isStealingEnabled: boolean = false;
    private isCaught: boolean = false; // New state to track if player is caught
    private isGameComplete: boolean = false; // New state to track if game is complete
    private gameRouteService: GameRouteService = new GameRouteService();

    public constructor() {
        super();
        this.shadow = this.attachShadow({ mode: "open" });
    }

    public connectedCallback(): void {
        this.render();
        this.setupEventListeners();
        void this.fetchCurrentState();
    }

    private render(): void {
        this.shadow.innerHTML = `
            <style>
                .game-container {
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                    justify-content: center;
                    padding: 20px;
                    position: relative;
                    font-family: 'Onesize', 'Courier New', monospace;
                }
                .game-title {
                    font-size: 24px;
                    margin-bottom: 15px;
                    color: #fff;
                }
                .game-board {
                    display: flex;
                    flex-direction: column;
                    gap: 10px;
                    background-color: #1a1a1a;
                    padding: 20px;
                    border-radius: 8px;
                    border: 2px solid #444;
                    box-shadow: 0 0 15px rgba(0,0,0,0.5);
                    width: 300px;
                }
                .counter {
                    margin: 10px 0;
                    font-size: 18px;
                    text-align: center;
                }
                .positions {
                    display: flex;
                    justify-content: space-between;
                    margin: 10px 0;
                    position: relative;
                    height: 60px;
                }
                .position {
                    width: 40px;
                    height: 40px;
                    background-color: #ddd;
                    border-radius: 4px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                }
                .player {
                    background-color: #ff8000;
                    color: white;
                }
                .guard {
                    background-color: #4a8af4;
                    color: white;
                }
                .key {
                    background-color: #f4d44a;
                    color: black;
                }
                .caught {
                    background-color: #ff0000;
                    color: white;
                }
                .complete {
                    background-color: #4caf50;
                    color: white;
                }
                .game-overlay {
                    display: none;
                    position: absolute;
                    top: 0;
                    left: 0;
                    right: 0;
                    bottom: 0;
                    background-color: rgba(0, 0, 0, 0.7);
                    justify-content: center;
                    align-items: center;
                    z-index: 10;
                    border-radius: 8px;
                }
                .game-complete-message {
                    color: white;
                    font-size: 20px;
                    text-align: center;
                    padding: 20px;
                }
                .button-container {
                    display: flex;
                    gap: 10px;
                    margin-top: 15px;
                    justify-content: center;
                }
                button {
                    font-family: 'Onesize', 'Courier New', monospace;
                    padding: 10px 15px;
                    font-size: 16px;
                    cursor: pointer;
                    border: none;
                    border-radius: 4px;
                    background-color: #4a8af4;
                    color: white;
                    transition: background-color 0.2s;
                }
                button:hover {
                    background-color: #3570d8;
                }
                button:disabled {
                    opacity: 0.5;
                    cursor: not-allowed;
                    background-color: #999;
                }
                #stealButton {
                    background-color: #f4d44a;
                    color: black;
                }
                #stealButton:hover {
                    background-color: #e6c73d;
                }
                #resetButton {
                    background-color: #f44a4a;
                }
                #resetButton:hover {
                    background-color: #ff6b6b;
                }
                .message {
                    margin-top: 15px;
                    font-style: italic;
                    min-height: 20px;
                    text-align: center;
                }
            </style>
            <div class="game-container">
                <div class="game-title">Hide from the guard</div>
                <div class="game-board">
                    <div class="counter">Sucesses: <span id="keysStolen">0</span>/5</div>
                    <div class="positions">
                        <div class="position" id="pos0">0</div>
                        <div class="position" id="pos1">1</div>
                        <div class="position" id="pos2">2</div>
                        <div class="position" id="pos3">3</div>
                        <div class="position" id="pos4">4</div>
                        
                        <!-- Game completion overlay -->
                        <div class="game-overlay" id="completionOverlay">
                            <div class="game-complete-message">

                            </div>
                        </div>
                    </div>
                    <div class="message" id="message"></div>
                    <div class="button-container">
                        <button id="moveLeftButton">Move Left</button>
                        <button id="moveRightButton">Move Right</button>
                        <button id="stealButton" disabled>Hide!</button>
                    </div>
                    <button id="resetButton">Reset Game</button>
                </div>
            </div>
        `;
    }

    private setupEventListeners(): void {
        const moveLeftButton: HTMLButtonElement | null = this.shadow.querySelector<HTMLButtonElement>("#moveLeftButton");
        const moveRightButton: HTMLButtonElement | null = this.shadow.querySelector<HTMLButtonElement>("#moveRightButton");
        const stealButton: HTMLButtonElement | null = this.shadow.querySelector<HTMLButtonElement>("#stealButton");
        const resetButton: HTMLButtonElement | null = this.shadow.querySelector<HTMLButtonElement>("#resetButton");

        if (moveLeftButton) moveLeftButton.addEventListener("click", () => this.movePlayer("left"));
        if (moveRightButton) moveRightButton.addEventListener("click", () => this.movePlayer("right"));
        if (stealButton) stealButton.addEventListener("click", () => void this.stealKey());
        if (resetButton) resetButton.addEventListener("click", () => void this.resetGame());
    }

    private movePlayer(direction: "left" | "right"): void {
        if (this.isCaught || this.isGameComplete) return; // Prevent movement if caught or game complete

        if (direction === "left" && this.playerPosition > 0) {
            this.playerPosition--;
        }
        else if (direction === "right" && this.playerPosition < 4) {
            this.playerPosition++;
        }

        this.updateGameBoard();
        this.checkGameState();
    }

    private checkGameState(): void {
        if (this.isGameComplete) {
            this.blockGameAfterCompletion();
            return;
        }

        // Check if player is caught first
        if (this.playerPosition === this.guardPosition) {
            this.isCaught = true;
            this.disableAllGameButtons();
            const messageElement: HTMLElement | null = this.shadow.querySelector<HTMLElement>("#message");
            if (messageElement) messageElement.textContent = "You've been caught by the guard! Game Over.";

            // Update visual representation to show caught state
            const playerPosition: Element | null = this.shadow.querySelector(`#pos${this.playerPosition}`);
            if (playerPosition) {
                playerPosition.className = "position caught";
                playerPosition.textContent = "X";
            }
            return;
        }

        // If not caught, check steal possibility
        this.checkStealPossibility();
    }

    private checkStealPossibility(): void {
        if (this.isCaught || this.isGameComplete) return; // Don't check if already caught or complete

        const stealButton: HTMLButtonElement | null = this.shadow.querySelector<HTMLButtonElement>("#stealButton");
        const moveLeftButton: HTMLButtonElement | null = this.shadow.querySelector<HTMLButtonElement>("#moveLeftButton");
        const moveRightButton: HTMLButtonElement | null = this.shadow.querySelector<HTMLButtonElement>("#moveRightButton");
        const messageElement: HTMLElement | null = this.shadow.querySelector<HTMLElement>("#message");

        // Player is adjacent to guard and guard has keys
        if (this.isAdjacentToGuard() && this.keysStolen < 5) {
            this.isStealingEnabled = true;

            // Enable steal button
            if (stealButton) stealButton.removeAttribute("disabled");

            // Disable movement buttons when Hide! is enabled
            if (moveLeftButton) moveLeftButton.setAttribute("disabled", "true");
            if (moveRightButton) moveRightButton.setAttribute("disabled", "true");

            if (messageElement) messageElement.textContent = "Hide now!";
        }
        else {
            this.isStealingEnabled = false;

            // Disable steal button
            if (stealButton) stealButton.setAttribute("disabled", "true");

            // Re-enable movement buttons based on position when Hide! is disabled
            this.updateMovementButtonsState();

            if (messageElement) {
                if (this.keysStolen >= 5) {
                    messageElement.textContent = "You've successfully hidden 5 times!";
                }
                else if (Math.abs(this.playerPosition - this.guardPosition) > 1) {
                    messageElement.textContent = "Get closer but don't let him catch you.";
                }
            }
        }
    }

    private disableAllGameButtons(): void {
        const moveLeftButton: HTMLButtonElement | null = this.shadow.querySelector<HTMLButtonElement>("#moveLeftButton");
        const moveRightButton: HTMLButtonElement | null = this.shadow.querySelector<HTMLButtonElement>("#moveRightButton");
        const stealButton: HTMLButtonElement | null = this.shadow.querySelector<HTMLButtonElement>("#stealButton");

        // Disable all game buttons except reset
        if (moveLeftButton) moveLeftButton.setAttribute("disabled", "true");
        if (moveRightButton) moveRightButton.setAttribute("disabled", "true");
        if (stealButton) stealButton.setAttribute("disabled", "true");
    }

    private isAdjacentToGuard(): boolean {
        return Math.abs(this.playerPosition - this.guardPosition) === 1;
    }

    private updateGameBoard(): void {
        // Skip visual updates if caught (handled in checkGameState)
        if (this.isCaught) return;

        // If game is complete, show the completion state
        if (this.isGameComplete) {
            this.blockGameAfterCompletion();
            return;
        }

        // Reset all positions
        for (let i: number = 0; i < 5; i++) {
            const position: Element | null = this.shadow.querySelector(`#pos${i}`);
            if (position) {
                position.className = "position";
                position.textContent = i.toString();
            }
        }

        // Set player position
        const playerPosition: Element | null = this.shadow.querySelector(`#pos${this.playerPosition}`);
        if (playerPosition) {
            playerPosition.className = "position player";
            playerPosition.textContent = "P";
        }

        // Set guard position
        const guardPosition: Element | null = this.shadow.querySelector(`#pos${this.guardPosition}`);
        if (guardPosition) {
            guardPosition.className = "position guard";
            guardPosition.textContent = "G";
        }

        // Update buttons state
        this.updateMovementButtonsState();
    }

    // Method to update movement buttons state
    private updateMovementButtonsState(): void {
        if (this.isCaught || this.isGameComplete) {
            this.disableAllGameButtons();
            return;
        }

        const moveLeftButton: HTMLButtonElement | null = this.shadow.querySelector<HTMLButtonElement>("#moveLeftButton");
        const moveRightButton: HTMLButtonElement | null = this.shadow.querySelector<HTMLButtonElement>("#moveRightButton");

        // If stealing is enabled, both buttons should be disabled
        if (this.isStealingEnabled) {
            if (moveLeftButton) moveLeftButton.setAttribute("disabled", "true");
            if (moveRightButton) moveRightButton.setAttribute("disabled", "true");
            return;
        }

        // Otherwise, enable/disable based on player position
        if (moveLeftButton) {
            if (this.playerPosition <= 0) {
                moveLeftButton.setAttribute("disabled", "true");
            }
            else {
                moveLeftButton.removeAttribute("disabled");
            }
        }

        if (moveRightButton) {
            if (this.playerPosition >= 4) {
                moveRightButton.setAttribute("disabled", "true");
            }
            else {
                moveRightButton.removeAttribute("disabled");
            }
        }
    }

    private async stealKey(): Promise<void> {
        if (!this.isStealingEnabled || this.isCaught || this.isGameComplete) return;

        try {
            const stealButton: HTMLButtonElement | null = this.shadow.querySelector<HTMLButtonElement>("#stealButton");
            if (stealButton) stealButton.setAttribute("disabled", "true");

            // Use gameRouteService instead of direct fetch
            const data: GameResponse | undefined = await this.gameRouteService.stealKey(this.playerPosition);

            if (data) {
                this.updateGameState(data.keysStolen, data.isComplete, data.guardPosition);

                setTimeout(() => {
                    if (!this.isCaught && !this.isGameComplete) {
                        this.checkGameState();
                    }
                }, 500);
            }
            else {
                console.error("Error updating game state: No data received");
            }
        }
        catch (error) {
            console.error(error);
        }
    }

    private async resetGame(): Promise<void> {
        try {
            // Use gameRouteService instead of direct fetch
            const data: GameResponse | undefined = await this.gameRouteService.resetKeyTheftGame();

            if (data) {
                // Reset local state
                this.playerPosition = 0;
                this.guardPosition = 2;
                this.isStealingEnabled = false;
                this.isCaught = false; // Reset caught state
                this.isGameComplete = false; // Reset complete state

                // Hide the completion overlay if it was shown
                const completionOverlay: HTMLElement | null = this.shadow.querySelector<HTMLElement>("#completionOverlay");
                if (completionOverlay) {
                    completionOverlay.style.display = "none";
                }

                this.updateGameState(data.keysStolen, data.isComplete, data.guardPosition);
            }
            else {
                console.error("Error resetting game: No data received");
            }
        }
        catch (error) {
            console.error(error);
        }
    }

    private async fetchCurrentState(): Promise<void> {
        try {
            // Use gameRouteService instead of direct fetch
            const data: GameResponse | undefined = await this.gameRouteService.getKeyTheftState();

            if (data) {
                this.updateGameState(data.keysStolen, data.isComplete, data.guardPosition);
            }
            else {
                console.error("Error fetching game state: No data received");
            }
        }
        catch (error) {
            console.error(error);
        }
    }

    private updateGameState(keysStolen: number, isComplete: boolean, guardPosition: number): void {
        this.keysStolen = keysStolen;
        this.guardPosition = guardPosition;
        this.isGameComplete = isComplete; // Update the game complete state

        const keysElement: Element | null = this.shadow.querySelector("#keysStolen");
        if (keysElement) keysElement.textContent = `${this.keysStolen}`;

        // Check if player position is same as guard position after state update
        if (this.playerPosition === this.guardPosition) {
            this.isCaught = true;
        }

        // Update visual representation
        this.updateGameBoard();
        this.checkGameState();

        if (isComplete) {
            // Block the game
            this.blockGameAfterCompletion();

            // Dispatch custom event when game is complete
            const gameCompleteEvent: CustomEvent<{
                success: boolean;
            }> = new CustomEvent("gameComplete", {
                bubbles: true,
                composed: true,
                detail: { success: true },
            });
            this.dispatchEvent(gameCompleteEvent);
        }
    }

    private blockGameAfterCompletion(): void {
        if (!this.isGameComplete) return;

        // Disable all interaction buttons
        this.disableAllGameButtons();

        // Update the message
        const messageElement: HTMLElement | null = this.shadow.querySelector<HTMLElement>("#message");
        if (messageElement) {
            messageElement.textContent = "You've successfully hidden from the guard and can now pickup the keys!";
        }

        // Show the completion overlay
        const completionOverlay: HTMLElement | null = this.shadow.querySelector<HTMLElement>("#completionOverlay");
        if (completionOverlay) {
            completionOverlay.style.display = "flex";
        }

        // Update visual representation to show success state
        // Make all positions green to indicate completion
        for (let i: number = 0; i < 5; i++) {
            const position: Element | null = this.shadow.querySelector(`#pos${i}`);
            if (position) {
                position.className = "position complete";
                position.textContent = "✓"; // Checkmark symbol
            }
        }
    }
}
